import { FieldValues, PathValue } from 'react-hook-form';
import { SingleSelectDropdown, SingleSelectDropdownProps } from 'ck-components';
import { useAuth, useDivisions } from 'ck-queries';
import { useEffect, useMemo } from 'react';

type Props<T extends FieldValues, K> = Omit<
  SingleSelectDropdownProps<T, K>,
  | 'defaultValue'
  | 'values'
  | 'labelFromValue'
  | 'onChange'
  | 'controlledConfig'
  | 'onChangeEffect'
  | 'hideValues'
>;

/**
 * DropdownSelector to pick a label-value pair for sections.
 */
export const TargetDivisionDropdownSelector = <T extends FieldValues, K>(
  props: Props<T, K>
) => {
  const { user } = useAuth();
  const Q_Divisions = useDivisions();
  const isAdmin = user?.roles.includes('SuperAdmin');
  const divisionOptions = useMemo(() => {
    if (Q_Divisions.data) {
      return Q_Divisions.data
        .filter((division) =>
          props.name !== 'createdByDivisionName'
            ? division.name !== 'Kåren' &&
              division.name !== 'THS' &&
              division.name !== 'Alumni'
            : division
        )
        .map((division) => division.unformattedName);
    }
    return [];
  }, [Q_Divisions.data]);

  useEffect(() => {
    const divisionName = user?.divisions[0]?.name;
    if (divisionName && props.name && divisionOptions.length > 0) {
      props.form.setValue(
        props.name,
        divisionName as PathValue<T, typeof props.name>
      );
    }
  }, [user, props.form, props.name, divisionOptions]);

  return (
    <SingleSelectDropdown
      {...props}
      label={props.label ?? 'Sektion / Division'}
      disabled={
        !Q_Divisions.data ||
        Q_Divisions.data.length < 1 ||
        Q_Divisions.isLoading ||
        !isAdmin
      }
      emptyStateLabel={'Välj sektion'}
      values={divisionOptions || []}
      labelFromValue={(value) => value}
    />
  );
};
